import * as React from 'react';
import { cn } from '../../lib/cn';
import { textToneStyles } from '../../lib/tone';
import type { PolymorphicComponentProps, TextAlign, TextTone, TextWeight } from '../../lib/types';

/**
 * `lead` is the one size above `body`, for introductory or emphasised copy that
 * is not a heading. Headings use `Heading`.
 */
export type TextVariant = 'lead' | 'body' | 'body-sm' | 'caption';

/** Omit to inherit the parent's family. */
type TextFont = 'sans' | 'mono';

interface TextOwnProps {
  /** @default 'body' */
  variant?: TextVariant;
  /** @default 'default' */
  tone?: TextTone;
  /** @default 'normal' */
  weight?: TextWeight;
  align?: TextAlign;
  /** For register ids and codes such as a HER-id, use `Identifier`. */
  font?: TextFont;
  /**
   * Renders tabular numerals, so numbers line up in columns and do not shift
   * as they change.
   * @default false
   */
  numeric?: boolean;
  /**
   * Keeps the text on one line and ends it with an ellipsis when it overflows.
   * Needs a block or flex-item element: on an inline `span` outside flex
   * layout it has no effect.
   * @default false
   */
  truncate?: boolean;
  className?: string;
}

export type TextProps<T extends React.ElementType = 'p'> = PolymorphicComponentProps<
  T,
  TextOwnProps
>;

const variantStyles = {
  lead: 'text-lg leading-normal',
  body: 'text-base leading-normal',
  'body-sm': 'text-sm leading-normal',
  caption: 'text-xs leading-normal',
} satisfies Record<TextVariant, string>;

const weightStyles = {
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
} satisfies Record<TextWeight, string>;

const alignStyles = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
} satisfies Record<TextAlign, string>;

const fontStyles = {
  sans: 'font-sans',
  mono: 'font-mono',
} satisfies Record<TextFont, string>;

/** Body text and captions. Renders a `p` unless `as` says otherwise. */
export function Text<T extends React.ElementType = 'p'>({
  as,
  variant = 'body',
  tone = 'default',
  weight = 'normal',
  align,
  font,
  numeric = false,
  truncate = false,
  className,
  ref,
  ...props
}: TextProps<T>) {
  const Component = as ?? 'p';

  return (
    <Component
      ref={ref}
      className={cn(
        variantStyles[variant],
        textToneStyles[tone],
        weightStyles[weight],
        align && alignStyles[align],
        font && fontStyles[font],
        numeric && 'tabular-nums',
        truncate && 'truncate',
        className,
      )}
      {...props}
    />
  );
}
